// Pure, CI-testable sidebar nav data + grouping logic.
//
// Extracted from Sidebar.jsx (which renders it) so the actual nav data and
// the filter/group algorithm can run under `node --test` (frontend's
// `npm run test:logic`, the suite CI actually runs) instead of only under
// vitest+JSX, which never runs in CI. This is what let the whole
// Settings/Administration group go silently unrendered for every user
// (including super_admin) — the group had no `section`, so it never matched
// any key in SECTION_META and was dropped at the grouping step, downstream
// of the (correct) permission filter. See sidebarNav.test.mjs for the
// regression guard.
//
// `icon` fields here are STRING keys (not lucide-react components) so this
// module stays free of React/JSX and importable under node --test. Sidebar.jsx
// resolves these strings back to components via a local ICONS map at render
// time.

import { hasAnyPermission } from '../utils/permissions.js'
import { SETTINGS_SECTIONS, SETTINGS_SECTION_ORDER } from '../pages/settings/sections.js'
import { SYSTEM_OVERVIEW_REQUIRE_ANY } from '../pages/systemOverviewAccess.mjs'

// Grouped sidebar sections — small uppercase micro-labels turning a flat
// nav list into sections. Groups are derived from what each nav item does;
// `section` is purely a rendering-order key, it doesn't gate anything.
export const SECTION_META = {
  overview: 'Overview',
  surveillance: 'Surveillance',
  data: 'Data & Models',
  admin: 'Administration'
}

export const NAV_ITEMS = [
  {
    name: 'Dashboard',
    href: '/app/dashboard',
    icon: 'LayoutDashboard',
    description: 'System overview and metrics',
    section: 'overview'
  },
  {
    // Sector incidence exceeds 24mo percentile thresholds
    name: 'Alerts',
    href: '/app/alerts',
    icon: 'BellRing',
    description: 'Sector incidence anomalies vs historical baseline',
    section: 'surveillance'
  },
  {
    name: 'Data Intake',
    href: '/app/ingest',
    icon: 'Inbox',
    description: 'Review pulled DHIS2/HMIS/NHIC batches before they drive the detector — manually, or auto-approved per source policy',
    section: 'data',
    requireAny: ['review_ingest']
  },
  {
    // Forecast Severity Map (restored 2026-08-14). Client-requested in the
    // RBC/MoH review: a Rwanda map of forecast alert severity by sector with
    // a WHO-standard legend, rolling next 6 months. Sits next to Alerts —
    // same surveillance instrument, seen geographically instead of as a list.
    name: 'Forecast Severity Map',
    href: '/app/forecast-map',
    icon: 'Map',
    description: 'Rwanda map of forecast alert severity by sector (WHO legend)',
    section: 'surveillance',
    // Same permission GET /api/alerts/by-sector enforces server-side
    // (services/api.js:468) — the nav link can't promise a page the API
    // will then 403 on.
    requireAny: ['view_predictions']
  },
  {
    // Settings IA rebuild (2026-07): relocated OUT of Settings (was
    // Settings > System > Alert Detector) to a top-level route next to
    // the Alerts list it tunes — this is an operational tuning tool, not
    // configuration. Same requireAny as before the move (unchanged).
    name: 'Alert Detector',
    href: '/app/alerts/detector',
    icon: 'Sliders',
    description: 'Switch + tune the WHO seasonal-month profile that drives the anomaly detector',
    section: 'surveillance',
    requireAny: ['manage_users']
  },
  {
    // Notebook-driven models nav. View Models browses notebooks/runs/PNG-
    // Excel preview; Upload notebook uploads + analyzes a new .ipynb.
    // Legacy joblib routes still callable by direct URL but not in nav.
    name: 'Models',
    icon: 'Brain',
    description: 'Notebook-driven prediction models',
    section: 'data',
    children: [
      {
        name: 'View Models',
        href: '/app/models',
        icon: 'Brain',
        description: 'Notebooks, runs, and forecast outputs'
      },
      {
        name: 'Upload notebook',
        href: '/app/models/notebooks',
        icon: 'Upload',
        description: 'Upload + analyze + activate a Jupyter notebook'
      },
      {
        // Real predicted-vs-observed accuracy, ranked per model run —
        // "which model do we trust for this disease". Never simulated;
        // see pages/models/modelAccuracyService.mjs.
        name: 'Model Accuracy',
        href: '/app/models/accuracy',
        icon: 'Trophy',
        description: 'Predicted-vs-observed accuracy leaderboard, ranked by real skill'
      }
    ]
  },
  {
    name: 'Data Management',
    icon: 'HardDrive',
    description: 'Import and manage datasets',
    section: 'data',
    children: [
      {
        name: 'Import Data',
        href: '/app/data/import',
        icon: 'Upload',
        description: 'Upload CSV datasets'
      },
      {
        name: 'Browse Data',
        href: '/app/data/browse',
        icon: 'Search',
        description: 'View and export imported data'
      }
    ]
  },
  {
    name: 'Schema',
    icon: 'Database',
    description: 'Database schema management',
    section: 'data',
    children: [
      {
        name: 'Schema Overview',
        href: '/app/schema',
        icon: 'Database',
        description: 'Schema versions and analysis'
      },
      {
        name: 'Tables',
        href: '/app/schema/tables',
        icon: 'Table',
        description: 'Manage database tables'
      },
      {
        name: 'Migrations',
        href: '/app/schema/migrations',
        icon: 'GitBranch',
        description: 'Database migrations'
      }
    ]
  },
  {
    name: 'Analytics',
    icon: 'BarChart3',
    description: 'Data visualization and analysis',
    section: 'data',
    children: [
      {
        name: 'Visual Creator',
        href: '/app/analytics/visual-creator',
        icon: 'TrendingUp',
        description: 'Create interactive visualizations'
      },
      {
        name: 'Saved Visuals',
        href: '/app/analytics/saved-visuals',
        icon: 'Bookmark',
        description: 'View and manage saved visualizations'
      },
    ]
  },

  // Administration — Settings IA rebuild (2026-07).
  //
  // Was a single 'Settings' group with 8 children (a flat mix of config
  // pages AND two operational tools — System Overview, Alert Detector).
  // That flat list is gone: System Overview and Alert Detector are now
  // top-level nav leaves of their own (Alert Detector moved up into
  // Surveillance, next to Alerts, above); everything else that's actual
  // CONFIGURATION collapses into a single 'Settings' leaf with NO
  // children — clicking it opens the unified Settings workspace
  // (SettingsLayout.jsx), which has its own two-level in-page nav
  // (Content / People / Maps & Locale / Alerts) instead of the sidebar
  // trying to represent that structure a second time.
  //
  // `section: 'admin'` on every item here — this is the field that
  // regressed once before (the whole group silently vanished for every
  // user, including super_admin, because it had no `section`). See
  // sidebarNav.test.mjs's bug-class guard.
  {
    // AdminPanel had no permission gate of its own pre-consolidation (any
    // authenticated user could reach /app/admin directly by URL, with no
    // nav link at all). QA follow-up (2026-07-20) added a real
    // route-level gate — SystemOverviewGate — because the settings
    // consolidation's index redirect made that ungated route easier to
    // stumble onto. requireAny is shared with that gate via
    // pages/systemOverviewAccess.mjs so the two can't drift.
    name: 'System Overview',
    href: '/app/system',
    icon: 'Shield',
    description: 'System health + admin tools',
    section: 'admin',
    requireAny: SYSTEM_OVERVIEW_REQUIRE_ANY
  },
  {
    // Super-admin security console over audit_schema.audit_logs. Gated on
    // view_audit_logs (migration 0035, granted to super_admin only) — the
    // same permission the read API enforces (services/auth/routes/audit.js)
    // and the page's own route guard checks.
    name: 'Audit log',
    href: '/app/audit',
    icon: 'ScrollText',
    description: 'Security · who did what, when',
    section: 'admin',
    requireAny: ['view_audit_logs']
  },
  {
    name: 'Settings',
    href: '/app/settings',
    icon: 'Settings',
    description: 'Content, people, maps, and localization',
    section: 'admin',
    // Union of all 4 Settings sections' requireAny — sourced from
    // sections.js (not re-typed) so this can't silently drift from what
    // SettingsLayout/SettingsIndexRedirect actually gate. No children:
    // this is a single link into the workspace; its own in-page nav
    // (SettingsLayout) handles which of Content/People/Maps & Locale/
    // Alerts (and which page within) the user can actually reach.
    requireAny: SETTINGS_SECTION_ORDER.flatMap((key) => SETTINGS_SECTIONS[key].requireAny)
  }
]

// Which single sidebar leaf should be highlighted "active" for a given
// pathname, out of every leaf href currently rendered in the nav.
//
// react-router's own NavLink `isActive` (without `end`) treats a link as
// active whenever the current path IS the link's href OR a descendant of
// it — so `/app/alerts` and `/app/alerts/detector` both light up on
// `/app/alerts/detector`, because `/app/alerts/detector` is a descendant
// of `/app/alerts`. Two siblings lit at once. `end` isn't a general fix
// either: it only ever matches the exact href, so a deeper unrelated route
// (e.g. `/app/alerts/123`, an alert detail page with no nav leaf of its
// own) would light up nothing instead of falling back to the closest
// ancestor leaf (`/app/alerts`).
//
// The actual rule is "most specific leaf wins": among every href that is
// either an exact match or an ancestor (prefix) of `pathname`, the longest
// one is the one truly being viewed. Pure + framework-free so it's
// covered by node --test, not just eyeballed in the browser.
export function resolveActiveHref(pathname, hrefs) {
  if (typeof pathname !== 'string' || !Array.isArray(hrefs)) return null
  let best = null
  for (const href of hrefs) {
    if (typeof href !== 'string' || !href) continue
    // Exact match, or `pathname` nested one-or-more segments below `href`.
    // The `+ '/'` guard is what stops `/app/alertsfoo` from being treated
    // as "under" `/app/alerts` — plain `startsWith(href)` would wrongly
    // match on shared string prefixes that aren't shared path segments.
    const matches = pathname === href || pathname.startsWith(`${href}/`)
    if (!matches) continue
    if (!best || href.length > best.length) best = href
  }
  return best
}

// Flattens the nav's two levels (top-level leaves, and parent-with-children
// groups' children) into a plain list of every href that can actually be
// highlighted — the input `resolveActiveHref` needs. Parent group headers
// themselves have no href (they're not navigable, just expand/collapse), so
// only leaves are collected.
export function collectLeafHrefs(items = []) {
  const hrefs = []
  for (const item of items) {
    if (item.href) hrefs.push(item.href)
    if (item.children) hrefs.push(...collectLeafHrefs(item.children))
  }
  return hrefs
}

// Permission-filter, then group into the labelled sections declared on each
// item. Identical to Sidebar.jsx's former inline logic. Grouping is a pure
// render-order concern — it re-labels/re-orders, it never adds, drops, or
// re-routes anything the permission filter already decided to show.
export function buildSidebarSections(user) {
  const hasAny = (list) => hasAnyPermission(user, list)

  const filteredNavigation = NAV_ITEMS
    .filter(item => hasAny(item.requireAny))
    .map(item => {
      if (!item.children) return item
      return {
        ...item,
        children: item.children.filter(c => hasAny(c.requireAny))
      }
    })
    // hide a group that ended up with zero visible children (defence
    // against accidentally-empty Settings/Admin)
    .filter(item => !item.children || item.children.length > 0)

  return Object.keys(SECTION_META)
    .map(key => ({
      key,
      label: SECTION_META[key],
      items: filteredNavigation.filter(item => item.section === key)
    }))
    .filter(section => section.items.length > 0)
}
